import React, {
  useContext, useEffect, useRef,
} from 'react';
import { Formik } from 'formik';
import {
  NativeBaseProvider, VStack, Text,
} from 'native-base';
import {
  Button, Dialog, Portal, TextInput,
} from 'react-native-paper';
import { useMutation } from '@apollo/client';
import { Animated, Keyboard, Platform } from 'react-native';
import { ModalContext } from '../../context/ModalContext';
import { transportationSchema } from '../../utils/yupSchema';
import {
  CREATE_TRANSPORTATION,
  UPDATE_TRANSPORTATION,
} from '../../gql/transportation/mutation';
import { GET_TRANSPORTATIONS } from '../../gql/transportation/query';
import Picker from '../../components/commons/Picker';

function ManageTransportation() {
  const {
    displayManageTransportationModal,
    toggleManageTransportationModal,
  } = useContext(ModalContext);
  const { title, transportation } = displayManageTransportationModal;
  const shift = useRef(new Animated.Value(0)).current;

  const [createTransportation] = useMutation(CREATE_TRANSPORTATION);
  const [updateTransportation] = useMutation(UPDATE_TRANSPORTATION);

  const isEditing = !!transportation;

  useEffect(() => {
    if (Platform.OS !== 'ios') {
      return () => { };
    }
    const keyboardWillShow = Keyboard.addListener('keyboardWillShow', (e) => {
      Animated.timing(shift, {
        toValue: -(e.endCoordinates.height / 2),
        duration: e.duration,
        useNativeDriver: true,
      }).start();
    });

    const keyboardWillHide = Keyboard.addListener('keyboardWillHide', (e) => {
      Animated.timing(shift, {
        toValue: 0,
        duration: e.duration,
        useNativeDriver: true,
      }).start();
    });

    return () => {
      keyboardWillShow.remove();
      keyboardWillHide.remove();
    };
  }, [shift]);

  return (
    <Formik
      initialValues={{
        name: transportation?.name || '',
        type: transportation?.type || 'CAR',
        seats: transportation?.seats?.toString() || '',
        color: transportation?.color || 'white',
      }}
      validateOnChange
      validationSchema={transportationSchema}
      onSubmit={async (values) => {
        const variables = {
          name: values.name,
          type: values.type,
          seats: parseInt(values.seats, 10),
          color: values.color,
        };

        if (isEditing) {
          await updateTransportation({
            variables: { id: transportation.id, ...variables },
            refetchQueries: [
              GET_TRANSPORTATIONS,
              'getTransportations',
            ],
          });
        } else {
          await createTransportation({
            variables,
            refetchQueries: [
              GET_TRANSPORTATIONS,
              'getTransportations',
            ],
          });
        }
        toggleManageTransportationModal();
      }}
    >
      {({
        handleSubmit, values, errors, setFieldValue, isValid,
      }) => (
        <Animated.View style={[{ flex: 1, transform: [{ translateY: shift }] }]}>
          <Dialog
            visible={displayManageTransportationModal.display}
            dismissable
            onDismiss={toggleManageTransportationModal}
          >
            <Dialog.Title>{title}</Dialog.Title>
            <Dialog.Content>
              <VStack space={4}>
                <VStack>
                  <Text mb={2}>Nom</Text>
                  <TextInput
                    style={{ backgroundColor: 'white' }}
                    onChangeText={(value) => setFieldValue('name', value)}
                    value={values.name}
                    mode="outlined"
                    label="Nom"
                  />
                  {!!errors.name && <Text color="red.500">{errors.name}</Text>}
                </VStack>

                <VStack>
                  <Text mb={2}>Type</Text>
                  <Picker
                    selectedValue={values.type}
                    onValueChange={(value) => setFieldValue('type', value)}
                  >
                    <Picker.Item label="Minibus" value="VAN" />
                    <Picker.Item label="Voiture" value="CAR" />
                  </Picker>
                  {!!errors.type && <Text color="red.500">{errors.type}</Text>}
                </VStack>

                <VStack>
                  <TextInput
                    style={{ backgroundColor: 'white' }}
                    onChangeText={(value) => setFieldValue('seats', value.replace(/[^0-9]/g, ''))}
                    value={values.seats}
                    keyboardType="numeric"
                    mode="outlined"
                    label="Nombre de places"
                  />
                  {!!errors.seats && <Text color="red.500">{errors.seats}</Text>}
                </VStack>

                <VStack>
                  <Text mb={2}>Couleur</Text>
                  <Picker
                    selectedValue={values.color}
                    onValueChange={(value) => setFieldValue('color', value)}
                  >
                    <Picker.Item label="Blanche" value="white" />
                    <Picker.Item label="Noire" value="black" />
                    <Picker.Item label="Rouge" value="red" />
                    <Picker.Item label="Vert" value="green" />
                    <Picker.Item label="Jaune" value="yellow" />
                    <Picker.Item label="Bleue" value="blue" />
                    <Picker.Item label="Orange" value="orange" />
                    <Picker.Item label="Rose" value="pink" />
                    <Picker.Item label="Gris" value="gray" />
                    <Picker.Item label="Marron" value="brown" />
                    <Picker.Item label="Violet" value="purple" />
                    <Picker.Item label="Cyan" value="cyan" />
                    <Picker.Item label="Turquoise" value="turquoise" />
                  </Picker>
                  {!!errors.color && <Text color="red.500">{errors.color}</Text>}
                </VStack>
              </VStack>
            </Dialog.Content>
            <Dialog.Actions>
              <Button onPress={toggleManageTransportationModal}>
                Annuler
              </Button>
              <Button disabled={!isValid} onPress={handleSubmit}>
                Sauvegarder
              </Button>
            </Dialog.Actions>
          </Dialog>
        </Animated.View>
      )}
    </Formik>
  );
}

export default function ManageTransportationModal() {
  const { displayManageTransportationModal } = useContext(ModalContext);
  if (!displayManageTransportationModal.display) {
    return null;
  }
  return (
    <Portal>
      <NativeBaseProvider>
        <ManageTransportation />
      </NativeBaseProvider>
    </Portal>
  );
}
